<template>
  <div class="card-performance-container">
    <!-- Header Title -->
    <div class="performance-header-wrapper mb-16">
      <h4 class="performance-title mb-15">Past Performance Results (5-Years)</h4>
    </div>

    <!-- 5 Fiscal Years Radio Group Selector (Optional / Standalone) -->
    <div v-if="showSelector" class="fiscal-selector-wrapper text-center my-16">
      <a-radio-group v-model="selectedFiscalYear" class="fiscal-year-radio-group" style="margin-bottom: 8px">
        <a-radio-button v-for="year in fiscalYearList" :key="year" :value="year">
          ปีงบประมาณ {{ year }}
        </a-radio-button>
      </a-radio-group>

      <div class="fiscal-period-wrapper">
        <span class="text-muted font-web f-13">
          {{ formatFiscalPeriod(selectedFiscalYear) }}
        </span>
        <a-tag v-if="isSelectedCurrentYear" color="orange" class="ml-8">
          ปีปัจจุบัน (ยังไม่มีข้อมูล)
        </a-tag>
      </div>
    </div>

    <!-- Performance Chart & Detail Card -->
    <a-card :bordered="false" class="header-solid performance-card" :bodyStyle="{ padding: '24px 20px 20px 20px' }">
      <!-- Performance Content & Chart (Always visible even when values are empty) -->
      <div class="performance-content">
        <!-- Selected Year Summary Banner -->
        <div class="selected-summary-box mb-20">
          <div class="summary-item">
            <span class="summary-label">ปีงบประมาณ</span>
            <span class="summary-val text-primary">{{ selectedFiscalYear }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">ผลการประเมิน</span>
            <span class="summary-val rating-badge">{{ (selectedYearData && selectedYearData.rating !== '-') ? selectedYearData.rating : '-' }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Performance</span>
            <span class="summary-val text-purple">{{ selectedYearData ? selectedYearData.performance : 0 }}%</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Core Value</span>
            <span class="summary-val text-yellow">{{ selectedYearData ? selectedYearData.coreValue : 0 }}%</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">คะแนนรวม</span>
            <span class="summary-val text-success font-weight-bold">
              {{ selectedYearData ? (selectedYearData.performance + selectedYearData.coreValue) : 0 }}%
            </span>
          </div>
        </div>

        <!-- Legend -->
        <div class="legend-container mb-24">
          <div class="legend-item mr-10">
            <span class="legend-dot dot-performance"></span>
            <span class="legend-label">Performance</span>
          </div>
          <div class="legend-item ml-24">
            <span class="legend-dot dot-core-value"></span>
            <span class="legend-label">Core Value</span>
          </div>
        </div>

        <!-- Chart Body -->
        <div class="chart-wrapper">
          <!-- Background Grid Lines (0, 20, 40, 60, 80, 100) -->
          <div class="grid-lines-container">
            <div v-for="tick in gridTicks" :key="'grid-' + tick" class="grid-line" :style="{ left: tick + '%' }"></div>
          </div>

          <!-- Stacked Bars List -->
          <div class="bars-container">
            <div v-for="item in performanceList" :key="'bar-' + item.year" class="bar-row mb-16"
              :class="{ 'is-selected-year': item.year === selectedFiscalYear }">
              <div class="year-label">
                {{ item.year }}
                <span v-if="item.year === selectedFiscalYear" class="active-indicator">&bull;</span>
              </div>
              <div class="bar-track">
                <div v-if="item.performance > 0 || item.coreValue > 0" class="stacked-bar">
                  <!-- Performance Segment (Purple) -->
                  <div
                    v-if="item.performance > 0"
                    class="bar-segment segment-performance"
                    :style="{ width: item.performance + '%' }"
                  >
                    <span v-if="item.rating && item.rating !== '-'" class="segment-rating">{{ item.rating }}</span>
                    <span class="segment-value">{{ item.performance }}</span>
                  </div>

                  <!-- Core Value Segment (Yellow) -->
                  <div
                    v-if="item.coreValue > 0"
                    class="bar-segment segment-core-value"
                    :style="{ width: item.coreValue + '%' }"
                  >
                    <span class="segment-value text-dark">{{ item.coreValue }}</span>
                  </div>
                </div>
                <div v-else class="empty-bar-track">
                  <span class="empty-bar-label font-web">ไม่มีข้อมูล</span>
                </div>
              </div>
            </div>
          </div>

          <!-- X-Axis Scale -->
          <div class="x-axis-wrapper">
            <div class="year-placeholder"></div>
            <div class="x-axis-scale">
              <span v-for="tick in gridTicks" :key="'axis-' + tick" class="axis-tick-label"
                :style="{ left: tick + '%' }">
                {{ tick }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
export default {
  name: 'CardPerformance',
  props: {
    dataPerformance: {
      type: Array,
      default: () => null,
    },
    defaultFiscalYear: {
      type: Number,
      default: null,
    },
    selectedYear: {
      type: Number,
      default: null,
    },
    showSelector: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const currentFY = this.calculateCurrentFiscalYear();
    // Default active year is current fiscal year - 1 (เพราะถ้าเป็นปัจจุบันยังจะไม่มีข้อมูล)
    const activeFY = this.selectedYear || this.defaultFiscalYear || (currentFY - 1);

    return {
      currentFiscalYear: currentFY,
      selectedFiscalYear: activeFY,
      gridTicks: [0, 20, 40, 60, 80, 100],
    };
  },
  watch: {
    selectedYear: {
      immediate: true,
      handler(val) {
        if (val) {
          this.selectedFiscalYear = val;
        }
      },
    },
  },
  computed: {
    // 5 fiscal years ending at current fiscal year
    fiscalYearList() {
      const current = this.currentFiscalYear;
      return [
        current - 4,
        current - 3,
        current - 2,
        current - 1,
        current,
      ];
    },
    isSelectedCurrentYear() {
      return this.selectedFiscalYear === this.currentFiscalYear;
    },
    performanceList() {
      if (this.dataPerformance && this.dataPerformance.length > 0) {
        return this.dataPerformance;
      }
      return this.fiscalYearList.map((year) => ({
        year,
        rating: '-',
        performance: 0,
        coreValue: 0,
      }));
    },
    selectedYearData() {
      return (
        this.performanceList.find((item) => item.year === this.selectedFiscalYear) || {
          year: this.selectedFiscalYear,
          rating: '-',
          performance: 0,
          coreValue: 0,
        }
      );
    },
  },
  methods: {
    calculateCurrentFiscalYear(date = new Date()) {
      const month = date.getMonth() + 1;
      const yearAD = date.getFullYear();
      const fiscalYearAD = month >= 10 ? yearAD + 1 : yearAD;
      return fiscalYearAD + 543;
    },
    formatFiscalPeriod(year) {
      if (!year) return '';
      const startYear = year - 1;
      return `ข้อมูล 1 ตุลาคม ${startYear} - 30 กันยายน ${year}`;
    },
  },
};
</script>

<style scoped>
.card-performance-container {
  width: 100%;
}

.performance-header-wrapper {
  display: flex;
  flex-direction: column;
}

.performance-title {
  font-size: 18px;
  font-weight: 700;
  color: #111111;
  line-height: 1.3;
}

.fiscal-selector-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 14px;
  margin-bottom: 18px;
  width: 100%;
  text-align: center;
}

.fiscal-year-radio-group {
  display: inline-flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

.fiscal-period-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-top: 2px;
}

.performance-card {
  border-radius: 20px;
  background-color: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

/* Selected Year Summary Box */
.selected-summary-box {
  display: flex;
  align-items: center;
  justify-content: space-around;
  background-color: #f8f9fe;
  border-radius: 12px;
  padding: 12px 16px;
  border: 1px solid #eef1f8;
  flex-wrap: wrap;
  gap: 12px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.summary-label {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 2px;
}

.summary-val {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.rating-badge {
  color: #5832e5;
  font-weight: 700;
}

.text-purple {
  color: #5832e5;
}

.text-yellow {
  color: #f0a300;
}

/* Legend */
.legend-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
}

.dot-performance {
  background-color: #5832e5;
}

.dot-core-value {
  background-color: #f9ba48;
}

.legend-label {
  font-size: 14px;
  font-weight: 500;
  color: #333333;
}

/* Chart */
.chart-wrapper {
  position: relative;
  width: 100%;
}

.grid-lines-container {
  position: absolute;
  top: 0;
  bottom: 24px;
  left: 55px;
  right: 10px;
  pointer-events: none;
}

.grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: #f0f0f0;
}

.bars-container {
  position: relative;
  z-index: 1;
}

.bar-row {
  display: flex;
  align-items: center;
  height: 38px;
  transition: all 0.2s ease;
  padding: 2px 4px;
  border-radius: 8px;
}

.bar-row.is-selected-year {
  background-color: rgba(88, 50, 229, 0.05);
}

.year-label {
  width: 55px;
  font-size: 14px;
  font-weight: 600;
  color: #333333;
  padding-right: 8px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.active-indicator {
  color: #5832e5;
  font-size: 18px;
  margin-left: 2px;
  line-height: 1;
}

.bar-track {
  flex: 1;
  position: relative;
  height: 32px;
}

.stacked-bar {
  display: flex;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  background-color: #f4f5f8;
}

.empty-bar-track {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 12px;
  background-color: #f4f5f8;
  border-radius: 16px;
  border: 1px dashed #d9dbe3;
}

.empty-bar-label {
  font-size: 11px;
  color: #a0a6b5;
  letter-spacing: 0.2px;
}

.bar-segment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  transition: width 0.3s ease;
  white-space: nowrap;
}

.segment-performance {
  background-color: #5832e5;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
}

.segment-core-value {
  background-color: #f9ba48;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  justify-content: flex-end;
}

.segment-rating {
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  overflow: hidden;
}

.segment-value {
  font-size: 13px;
  font-weight: 700;
  margin-left: 6px;
}

.text-dark {
  color: #222222;
}

/* X-Axis */
.x-axis-wrapper {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.year-placeholder {
  width: 55px;
}

.x-axis-scale {
  flex: 1;
  position: relative;
  height: 20px;
}

.axis-tick-label {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 12px;
  color: #888888;
}

.no-data-wrapper {
  background-color: #fafbfc;
  border-radius: 12px;
  border: 1px dashed #e1e4e8;
}
</style>
